/**
 * Gradient Design Tokens
 *
 * Single source of truth for gradient backgrounds, derived from the color
 * tokens. Consumed by the MUI theme (`theme.gradients`) and directly by
 * Tailwind-first code that needs a raw gradient string without `useTheme`.
 */
import { alpha } from '@gto/helpers/alpha'

import { colors } from './colors'

export const gradients = Object.freeze({
  playingCard: {
    default: `linear-gradient(to bottom, #ffffff, #f5f5f5)`,
  },
  primary: {
    horizontal: (alphaStart = 0.08, alphaEnd = 0.01) =>
      `linear-gradient(90deg, ${alpha(colors.primary.main, alphaStart)} 0%, ${alpha(
        colors.primary.main,
        alphaEnd,
      )} 100%)`,
    horizontalAnimated: (alphaStart = 0.08, alphaEnd = 0.01) =>
      `linear-gradient(90deg, ${alpha(colors.primary.main, alphaStart)} 0%, ${alpha(
        colors.primary.main,
        alphaEnd,
      )} 49.9%, transparent 50%, transparent 100%)`,
    vertical: (alphaStart = 0.08, alphaEnd = 0.01) =>
      `linear-gradient(180deg, ${alpha(colors.primary.main, alphaStart)} 0%, ${alpha(
        colors.primary.main,
        alphaEnd,
      )} 100%)`,
  },
  overlay: {
    darkRight: (color?: string) =>
      `linear-gradient(270deg, ${color ?? colors.paper.elevation1} 0%, rgba(18, 18, 20, 0.00) 100%)`,
    darkLeft: (color?: string) =>
      `linear-gradient(90deg, ${color ?? colors.paper.elevation1} 0%, rgba(18, 18, 20, 0.00) 100%)`,
    /**
     * "More content below" fade: the given surface colour at the BOTTOM edge fading upward to
     * the SAME colour at zero alpha, so content dissolves into whatever background it sits on
     * (never toward black — see `darkRight`'s transparent-paper convention). Consumed per
     * elevation by the `overflow-fade-*` background images in the Tailwind preset.
     */
    bottomFade: (color?: string) =>
      `linear-gradient(to top, ${color ?? colors.paper.elevation1} 0%, ${alpha(
        color ?? colors.paper.elevation1,
        0,
      )} 100%)`,
    /**
     * Mirror of `bottomFade`: opaque at the TOP edge, fading down to the same colour
     * at zero alpha (never toward black — see `darkRight`). For a rule or border that
     * begins partway down a page, painting the surface back over its first pixels is
     * what turns the snap into a taper.
     */
    topFade: (color?: string) =>
      `linear-gradient(to bottom, ${color ?? colors.paper.elevation1} 0%, ${alpha(
        color ?? colors.paper.elevation1,
        0,
      )} 100%)`,
    endFade: (color?: string) =>
      `linear-gradient(to left, ${color ?? colors.paper.elevation1} 0%, ${alpha(
        color ?? colors.paper.elevation1,
        0,
      )} 100%)`,
  },
  paper1: `linear-gradient(180deg, ${colors.paper.elevation1} 0%, #0F0F10 100%)`,
  /**
   * Diagonal hatch for the dead margin beside a centred marketing column — reads
   * as "outside the content" without drawing a border. A token rather than a
   * per-page arbitrary value because the marketing wrapper and the Fair Play page
   * frame have to hatch IDENTICALLY: they meet at the page edges, and two copies
   * drifting apart in angle or opacity shows up as a seam. 1px of tint every 9px
   * is the point where the texture is legible at 2% but still reads as flat.
   */
  hatch: `repeating-linear-gradient(-45deg, transparent, transparent 8px, ${alpha('#ffffff', 0.02)} 8px, ${alpha('#ffffff', 0.02)} 9px)`,
})
